import { useCallback, useEffect, useState } from 'react';
import { Order, OrderReturnStatus, OrderStatus } from '../../../clients/quipli-service/order/order';
import { Location } from '../../../clients/quipli-service/location/location';
import './order-payments-table.scss';
import { displayMoneyFormat } from '../../../util/util';
import _, { debounce } from 'lodash';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import {
  OrderPayment,
  PaymentReason,
  PaymentStatus,
  RefundReason,
} from '../../../clients/quipli-service/payment/payment';
import { usePaymentApiV1 } from '../../../clients/quipli-service/payment/payment-api-v1';
import { RefundModal } from './refund-modal';
import { useAdmin } from '../../../hooks/use-admin-context';
import { toast } from '../../../util/toasts';
import { Field } from '../../../components/field/field';
import { TextArea } from '../../../components/textarea/textarea';
import { OrderSection } from '../order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import { DataTable } from '../../../components/table/data-table';
import { Badge } from '../../../components/badge/badge';
import classNames from 'classnames';
import { isMobileOnly } from 'react-device-detect';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { useOrderApiV1 } from '../../../clients/quipli-service/order/order-api-v1';

export const OrderSecurityDeposit = ({
  order,
  location,
  refresh,
}: {
  order: Order;
  location: Location;
  refresh: () => void;
}) => {
  const adminContext = useAdmin();

  const [securityDeposits, setSecurityDeposits] = useState<OrderPayment[]>();
  const [paymentToRefund, setPaymentToRefund] = useState<OrderPayment>();
  const [paymentToCapture, setPaymentToCapture] = useState<OrderPayment>();
  const [placeHoldModalOpen, setPlaceHoldModalOpen] = useState<boolean>(false);
  const { createSecurityDeposit } = useOrderApiV1();

  const shouldPlaceSecurityDeposit =
    order.paymentMethodType === 'CreditCard' &&
    ![OrderReturnStatus.Complete, OrderReturnStatus.OffRent].includes(order.returnStatus) &&
    order.rentalSecurity > 0 &&
    OrderStatus.Approved === order.status;

  function getRefundedAmount(payment: OrderPayment): number {
    return _.sum(
      order?.payments.filter((p) => p.reason === 'Refund' && p.refundedPaymentId === payment.id).map((p) => p.amount),
    );
  }

  const placeDeposit = useCallback(
    debounce(async () => {
      try {
        await createSecurityDeposit(order.id);
      } catch (ex: any) {
        toast(ex.message, 'error');
      } finally {
        setPlaceHoldModalOpen(false);
        refresh();
      }
    }, 600),
    [],
  );

  useEffect(() => {
    if (order) {
      setSecurityDeposits(
        order.payments.filter(
          (p) =>
            p.reason === PaymentReason.SecurityDeposit &&
            getRefundedAmount(p) < p.amount &&
            p.status !== PaymentStatus.failed &&
            p.gatewayStatus !== 'canceled',
        ),
      );
    }
  }, [order]);

  function closeModals(): void {
    setPaymentToRefund(undefined);
    setPaymentToCapture(undefined);
  }

  return (
    <>
      {location && ((securityDeposits && securityDeposits.length) || shouldPlaceSecurityDeposit) ? (
        <OrderSection header="Security Deposit" className="flex flex-col gap-[8px]">
          <div
            className={classNames({
              'border border-solid border-grey-200 rounded-[9px]': !isMobileOnly,
            })}
          >
            {securityDeposits?.length ? (
              <DataTable
                columnNames={[
                  { key: 'date', label: 'Date Added' },
                  { key: 'amount', label: 'Amount', showRule: 'OnlyDesktop' },
                  { key: 'status', label: 'Status' },
                  { key: 'comments', label: 'Comments' },
                  { key: 'actions', label: '' },
                ]}
                data={securityDeposits.map((payment) => ({
                  mobileHeader: (
                    <div className="flex gap-[4px]">
                      <Typography type="text-md" weight="medium">
                        {displayMoneyFormat(payment.amount - getRefundedAmount(payment), location.currency)}
                      </Typography>
                      <Badge
                        className="capitalize"
                        type={payment.status === PaymentStatus.pending ? 'warning' : 'success'}
                        text={payment.status}
                      />
                    </div>
                  ),
                  fields: {
                    date: (
                      <div className="flex flex-col">
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {payment.date.toFormat('DD')}
                        </Typography>
                        <Typography type="text-sm" className="text-grey-500">
                          {payment.date.toFormat('t')}
                        </Typography>
                      </div>
                    ),
                    amount: displayMoneyFormat(payment.amount - getRefundedAmount(payment), location.currency),
                    status:
                      payment.method === 'Stripe' && payment.status !== PaymentStatus.failed
                        ? payment.gatewayStatus
                        : payment.status,
                    comments: payment.comments?.replaceAll('_', ' ') || '',
                    actions: adminContext.hasPermissions('Refund Upcharge Order') && (
                      <div
                        className={classNames('flex gap-1', {
                          'flex-col w-full pt-[8px]': isMobileOnly,
                        })}
                      >
                        {payment.status === PaymentStatus.pending ? (
                          <Button
                            className={classNames('button-primary', {
                              'w-full': isMobileOnly,
                            })}
                            onClick={() => setPaymentToCapture(payment)}
                            data-cy="charge_deposit"
                          >
                            Charge
                          </Button>
                        ) : undefined}
                        <Button
                          className={classNames('button-secondary', {
                            'w-full': isMobileOnly,
                          })}
                          onClick={() => setPaymentToRefund(payment)}
                          data-cy="release_hold"
                        >
                          {payment.status === PaymentStatus.pending ? 'Release Hold' : 'Refund Deposit'}
                        </Button>
                      </div>
                    ),
                  },
                }))}
              />
            ) : (
              shouldPlaceSecurityDeposit && (
                <Button
                  className={classNames('button-primary m-2', {
                    'w-full': isMobileOnly,
                  })}
                  onClick={() => setPlaceHoldModalOpen(true)}
                  data-cy="place_deposit"
                >
                  Place Security Deposit
                </Button>
              )
            )}
          </div>

          {paymentToRefund && paymentToRefund.status === PaymentStatus.succeeded && (
            <RefundModal
              isOpen={true}
              close={() => {
                closeModals();
                refresh();
              }}
              payments={[paymentToRefund]}
              refundReasons={['Security Deposit Refund']}
            ></RefundModal>
          )}
          {paymentToRefund && paymentToRefund.status === PaymentStatus.pending && (
            <div className="release-hold-modal">
              <ReleaseHoldModal
                isOpen={true}
                close={closeModals}
                payment={paymentToRefund}
                refresh={refresh}
              ></ReleaseHoldModal>
            </div>
          )}
          {paymentToCapture && (
            <div className="capture-hold-modal">
              <CaptureHoldModal
                isOpen={true}
                close={closeModals}
                payment={paymentToCapture}
                refresh={refresh}
              ></CaptureHoldModal>
            </div>
          )}
          {placeHoldModalOpen && (
            <ConfirmModal
              isOpen={true}
              close={() => {
                setPlaceHoldModalOpen(false);
                refresh();
              }}
              prompt={`Are you sure you want to place a security deposit for this order?`}
              onConfirm={async () => {
                await placeDeposit();
              }}
            />
          )}
        </OrderSection>
      ) : (
        <></>
      )}
    </>
  );
};

const ReleaseHoldModal = ({
  isOpen,
  close,
  payment,
  refresh,
}: {
  isOpen: boolean;
  close: (payment?: OrderPayment) => void;
  payment: OrderPayment;
  refresh: () => void;
}) => {
  const { createRefund } = usePaymentApiV1();
  const [comments, setComments] = useState<string>();
  const [isRefunding, setIsRefunding] = useState<boolean>(false);

  async function doRefund() {
    setIsRefunding(true);
    try {
      const result = await createRefund({
        comments: 'Security Deposit Refund' + (comments ? ` (${comments})` : ''),
        partialAmount: payment.amount,
        paymentIdToRefund: payment.id,
        reason: RefundReason.Other,
      });

      refresh();

      toast('Security deposit hold released');
      close(result.payment);
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsRefunding(false);
  }

  return (
    <Modal
      title="Release Security Deposit Hold"
      additionalText="Are you sure you want to release the security deposit hold?"
      actions={
        <>
          <Button className="button button-secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button className="button button-yellow" onClick={() => doRefund()} disabled={isRefunding}>
            Release Hold
          </Button>
        </>
      }
      isOpen={isOpen}
      close={close}
    >
      <TextArea
        label="Comments"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        autoResize={false}
        rows={3}
        className="text-sm"
      />
    </Modal>
  );
};

const CaptureHoldModal = ({
  isOpen,
  close,
  payment,
  refresh,
}: {
  isOpen: boolean;
  close: (payment?: OrderPayment) => void;
  payment: OrderPayment;
  refresh: () => void;
}) => {
  const { chargeHold } = usePaymentApiV1();
  const [amount, setAmount] = useState<string>(payment.amount.toString());
  const [comments, setComments] = useState<string>();
  const [isCapturing, setIsCapturing] = useState<boolean>(false);

  async function doCapture() {
    setIsCapturing(true);
    try {
      const result = await chargeHold({
        paymentId: payment.id,
        comments: 'Security Deposit Charge' + (comments ? ` (${comments})` : ''),
        amount: parseFloat(amount),
      });

      refresh();

      toast('Security deposit captured');
      close(result.payment);
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsCapturing(false);
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Capture Security Deposit Hold"
      additionalText="Are you sure you want to capture the security deposit hold?"
      actions={
        <>
          <Button className="button button-secondary" onClick={() => close()}>
            Cancel
          </Button>
          <Button className="button button-yellow" onClick={() => doCapture()} disabled={isCapturing}>
            Capture
          </Button>
        </>
      }
      close={close}
    >
      <Field label="Amount" isRequired type="currency" value={amount} onChange={(value) => setAmount(value)} />

      <TextArea
        label="Comments"
        value={comments}
        onChange={(e) => setComments(e.target.value)}
        autoResize={false}
        rows={3}
        className="text-sm"
      />
    </Modal>
  );
};
